(ns practitest.frontend.components.view-editor
  (:require
    [clojure.string :as str]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.i18n :refer [get-translation]]
    [practitest.frontend.components2.spinners :as spinners]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.common.permissions :as permissions]
    [practitest.frontend.components.column-picker :as column-picker]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.modal :as modal]
    [practitest.frontend.components.selector :as selector2]
    [practitest.frontend.components.tooltip :as tooltip]
    [practitest.frontend.components.view-editor.basic-builder :as bb]
    [practitest.frontend.components.view-editor.events :as events]
    [practitest.frontend.components.view-editor.subs :as view-editor.subs]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.components2.components-provider :refer [application-provider]]
    [practitest.frontend.pt-ts-bridge :as pt-ts-bridge]
    [practitest.frontend.ptql-editor :as ptql-editor]
    [practitest.frontend.routing.backend-routes :as broutes]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]))

;; ---- views ----

(defn- track-action! [action]
  (mixpanel/track "Filter Editor" {"Action Name" action}))

(defn- track-toggle! [expanded-action collapsed-action]
  (fn [e]
    (track-action! (if (.. e -target -open) expanded-action collapsed-action))))

(defn- tooltip-icon [text]
  [tooltip/mux-tooltip
   {}
   text
   (fn [props]
     [:span.view-editor-tooltip-icon props
      [icons/mux-info-circle-nofill]])])

;; TODO: replace with non-bootstrap component once we get rid of bootstrap
(defn- view-as-ptql-switch [form]
  (let [render-mode @(re-frame/subscribe [::view-editor.subs/filter-render-mode])
        ptql-error @(re-frame/subscribe [::view-editor.subs/ptql-error])
        can-basic? (and (nil? ptql-error) (bb/fits? (:view-ast form)))
        ptql? (or (= :advanced render-mode) (not can-basic?))]
    [:div.form-switch.view-editor-render-mode-switch
       [:label.form-check-label {:for "view-editor-as-ptql"}
        (get-translation "view-editor.render-mode.ptql-mode" "PTQL Mode")]
       [:input.form-check-input
        {:type "checkbox"
         :id "view-editor-as-ptql"
         :checked ptql?
         :disabled (not can-basic?)
         :on-change (fn [e]
                      (let [advanced? (dom/event-value e)]
                        (track-action! (if advanced? "PTQL Mode" "Basic Mode"))
                        (re-frame/dispatch [::events/set-filter-render-mode
                                            (if advanced? :advanced :basic)])))}]
       (when-not can-basic?
         [tooltip-icon
          (get-translation "view-editor.render-mode.basic-unavailable.tooltip"
                           "Basic mode isn't available for this filter")])]))

(defn- ptql-section [{:keys [value on-change on-parse-result
                             parse-error read-only? auto-focus? source-entity]}]
  (let [fields-by-entity @(re-frame/subscribe [::subs/fields-by-ptql-entity])
        users @(re-frame/subscribe [::subs/users])
        groups @(re-frame/subscribe [::subs/groups])
        tags @(re-frame/subscribe [::subs/tags])]
    [:div.view-editor-section
     {:class (if read-only? "is-read-only" "is-editable")}
     [ptql-editor/editor
      {:value value
       :on-change on-change
       :on-parse-result on-parse-result
       :fields-by-entity fields-by-entity
       :source-entity source-entity
       :users users
       :groups groups
       :tags (mapv first tags)
       :read-only? read-only?
       :auto-focus? auto-focus?}]
     (when-not read-only?
       [:div.view-editor-ptql-hint
        [:kbd "Ctrl"] " + " [:kbd "Space"]
        " for suggestions"])
     (when parse-error
       [:div.view-editor-error-note parse-error])]))

(defn- auto-filter-row [idx value options removable? can-add?]
  [:div.auto-filter-row
   (when (pos? idx)
     [:span.auto-filter-row-indent [icons/mux-chevron-right]])
   [selector2/new-select
    {:items options
     :value (some #(when (= (:value %) value) %) options)
     :label-fn :label
     :key-fn :value
     :placeholder (get-translation "view-editor.auto-filter.none" "Select List Field")
     :on-change #(re-frame/dispatch [::events/set-auto-filter-field idx (:value %)])
     :test-id "filter-criteria-select"
     :data-level (str idx)}]
   (when (= value "linked_milestones")
     [tooltip-icon (get-translation "view-editor.auto-filter.milestone-note.tooltip"
                                    "The milestone index shows up to 50 milestones based on their dates - active ones first, then upcoming and completed ones.")])
   (when removable?
     [buttons/icon-button
      {:icon [icons/mux-x]
       :tooltip (get-translation "generic.button.remove" "Remove")
       :on-click #(re-frame/dispatch [::events/remove-auto-filter-level idx])}])
   (when can-add?
     [buttons/button
      {:tertiary? true
       :icon [icons/mux-reg-plus]
       :text (get-translation "view-editor.auto-filter.add-child-field" "Add Child Field")
       :test-id "filter-add-child-btn"
       :on-click #(re-frame/dispatch [::events/add-auto-filter-level])}])])

(defn- auto-filter-section [form]
  (let [options @(re-frame/subscribe [::view-editor.subs/auto-filter-options])
        max-levels @(re-frame/subscribe [::view-editor.subs/auto-filter-max-levels])
        levels (or (:auto-filter form) [])
        levels (if (empty? levels) [nil] levels)
        last-filled? (not (str/blank? (last levels)))
        last-idx (dec (count levels))
        can-add? (and last-filled? (< (count levels) max-levels))]
    [:div.view-editor-section-row
     [:div.view-editor-section-gutter
      [:span (get-translation "view-editor.select-a-field" "Select a Field")]]
     [:div.view-editor-section-content
      (doall
        (map-indexed
          (fn [idx v]
            (let [taken (disj (set levels) v nil)
                  row-options (remove (comp taken :value) options)]
              ^{:key idx} [auto-filter-row idx v row-options
                           (and (pos? idx) (= idx last-idx))
                           (and can-add? (= idx last-idx))]))
          levels))

      [:label.m-checkbox-label
       [:input {:type "checkbox"
                :checked (boolean (:hide-empty-auto-filters form))
                :on-change #(re-frame/dispatch [::events/set-hide-empty-auto-filters (dom/event-value %)])}]
       (get-translation "view-editor.auto-filter.hide-empty" "Hide empty values in Filter")
       [:span {:on-click dom/prevent-default}
        [tooltip-icon
         (get-translation "view-editor.auto-filter.hide-empty.tooltip"
                          "Don't show auto-filter values that have no matching entities.")]]]
      [:div.view-editor-auto-filter-note
       [:span (get-translation "view-editor.auto-filter.values-limit-note"
                               "Note: Each Auto Filter is limited to 50 values. If the selected field contains more than 50 values, only the 50 latest values will be used.")]]]]))

(defn- name-row [form mode]
  [:div.view-editor-name-row
   [:label.view-editor-name-label {:for "view-editor-name"}
    (get-translation "view-editor.name" "Filter Name")]
   [:input.m-input
    {:id "view-editor-name"
     :type "text"
     :title (:name form)
     :auto-focus (= mode :create)
     :value (:name form)
     :on-change #(re-frame/dispatch [::events/name-changed (dom/event-value %)])}]
   (let [can-public? (permissions/has-permission? "custom_views_own")]
     [:label.view-editor-public-checkbox
      {:class (when-not can-public? "disabled")
       :title (when-not can-public?
                (get-translation "view-editor.is-public.no-permission"
                                 "You don't have permission to create public filters"))}
      [:input {:type "checkbox"
               :checked (boolean (:public? form))
               :disabled (not can-public?)
               :on-change (fn [e]
                            (let [public? (dom/event-value e)]
                              (track-action! (if public? "Public Selection" "Private Selection"))
                              (re-frame/dispatch [::events/public-changed public?])))}]
      (get-translation "view-editor.is-public" "Public")
      [tooltip-icon (get-translation "view-editor.is-public.tooltip"
                                     "Public views are visible to everyone with access to the project. Otherwise only you can see it.")]])])

(defn- filter-type-row [form]
  (let [limit-reached? @(re-frame/subscribe [::view-editor.subs/auto-filter-limit-reached?])
        mode (or (:mode form) :ptql)
        af-disabled? (and limit-reached? (not= mode :auto-filter))]
    [:div.view-editor-filter-type-row
     [:span.view-editor-row-label (get-translation "view-editor.filter-type" "Filter Type")]
     [:label.view-editor-filter-type-option
      [:input {:type "radio"
               :name "view-editor-filter-type"
               :checked (= mode :ptql)
               :on-change #(re-frame/dispatch [::events/set-mode :ptql])}]
      " " (get-translation "view-editor.filter-type.manual" "Manual")]
     [:label.view-editor-filter-type-option
      [:input (dom/add-test-id
                {:type "radio"
                 :name "view-editor-filter-type"
                 :checked (= mode :auto-filter)
                 :disabled af-disabled?
                 :on-change #(re-frame/dispatch [::events/set-mode :auto-filter])}
                "filter-auto-filter-checkbox")]
      " " (get-translation "view-editor.filter-type.auto-filter" "Auto Filter")]
     (when af-disabled?
       [:div.view-editor-filter-type-warning
        (get-translation "view-editor.auto-filter.limit-reached"
                         "Auto-filter limit reached for this entity type")])]))

(defn- filter-criteria-header [form]
  [:div.view-editor-criteria-header-row
   [:span.view-editor-section-title (get-translation "view-editor.filter-criteria" "Filter Criteria")]
   [view-as-ptql-switch form]])

(defn- collapsible-card [{:keys [open? label tooltip hint track-expanded track-collapsed]} summary-extra content]
  [:details.view-editor-collapsible-card
   {:open open?
    :on-toggle (track-toggle! track-expanded track-collapsed)}
   [:summary.view-editor-collapsible-summary
    [:span.view-editor-chevron-icon [icons/mux-chevron-right]]
    [:span.view-editor-collapsible-title
     label
     [tooltip-icon tooltip]]
    summary-extra
    [:span.view-editor-collapsible-hint hint]]
   content])

(defn- parent-section [form source-entity]
  (let [full-path (when (seq (:ancestors-path form))
                    (str/join " / " (map #(or (:name %) "...") (:ancestors-path form))))
        ptql (:ancestors-ptql-optimized form)]
    [collapsible-card
     {:label (get-translation "view-editor.parent" "Parent")
      :tooltip (get-translation "view-editor.parent.tooltip"
                                "This filter inherits the criteria of its parent filters.")
      :hint (get-translation "view-editor.combined.read-only" "Read Only")
      :track-expanded "Parent Expanded"
      :track-collapsed "Parent Collapsed"}
     (if full-path
       [tooltip/mux-tooltip {} full-path
        (fn [props]
          [:div.view-editor-parent-display props full-path])]
       [:div.view-editor-parent-display
        (get-translation "view-editor.parent.none" "No parent filter")])
     (cond
       (nil? full-path)
       [:div.view-editor-section-empty-note
        (get-translation "view-editor.parent.none.note"
                         "This filter is at the top level and inherits no criteria.")]

       (str/blank? ptql)
       [:div.view-editor-section-empty-note
        (get-translation "view-editor.parent.no-criteria"
                         "The parent filter has no criteria of its own.")]

       :else
       [ptql-section {:value ptql :read-only? true :source-entity source-entity}])]))

(defn- effective-section [form source-entity & [{:keys [open?]}]]
  (let [recomputing? @(re-frame/subscribe [::view-editor.subs/recomputing?])
        ptql (:combined-ptql-optimized form)]
    [collapsible-card
     {:open? open?
      :label (get-translation "view-editor.effective" "Effective")
      :tooltip (get-translation "view-editor.combined.tooltip"
                                "The effective filter combines this filter with all parent filters.")
      :hint (if recomputing?
              (get-translation "view-editor.combined-computing" "Computing...")
              (get-translation "view-editor.combined.read-only" "Read Only"))
      :track-expanded "Effective Filter Expanded"
      :track-collapsed "Effective Filter Collapsed"}
     nil
     (if (str/blank? ptql)
       [:div.view-editor-section-empty-note
        (get-translation "view-editor.effective.no-criteria" "No criteria defined yet.")]
       [ptql-section {:value ptql :read-only? true :source-entity source-entity}])]))

(defn- parent-picker-row [form source-entity]
  (let [view-id @(re-frame/subscribe [::view-editor.subs/view-id])
        parent-id @(re-frame/subscribe [::view-editor.subs/parent-id])
        parent-public? @(re-frame/subscribe [::view-editor.subs/parent-public?])
        project-id @(re-frame/subscribe [::subs/current-project-id])
        user-id (:id @(re-frame/subscribe [::subs/current-user]))
        parent-name (some-> (:ancestors-path form) (last) :name)
        full-path (when (seq (:ancestors-path form))
                    (str/join " / " (map #(or (:name %) "...") (:ancestors-path form))))]
    [:div.view-editor-section-row
     [:div.view-editor-section-gutter
      [:span (get-translation "view-editor.move.new-parent" "New Parent")]]
     [:div.view-editor-section-content.view-editor-parent-picker
      [application-provider
       [:> pt-ts-bridge/GenericSearchableTree
        {:scope (clj->js {:userId (str user-id)
                          :projectId (str project-id)
                          :module source-entity
                          :entity source-entity
                          :tab "filters"})
         :urls (clj->js {:fetchRootTreeUrl (broutes/get-url "custom_views#by_entity" {:p_id project-id}
                                                            {:entity_name source-entity :views_count -1})
                         :fetchTreeNode (broutes/get-url "custom_views#fetch_filter" {:p_id project-id}
                                                         {:entity_name source-entity})
                         :search (broutes/get-url "custom_views#search" {:p_id project-id}
                                                  {:entity_name source-entity})})
         :idsToRemove #js [(str view-id)]
         :excludeAutoFilters true
         :initialSelectedItem (when parent-id #js {:id (str parent-id) :name (or parent-name "")})
         :selectedValue (when parent-id (str parent-id))
         :displayName (when parent-id full-path)
         :onChange #(re-frame/dispatch [::events/set-parent-filter (js->clj % :keywordize-keys true)])}]]
      (when (and (:public? form) (not parent-public?))
        [:div.view-editor-filter-type-warning
         (get-translation "view-editor.move.private-parent"
                          "A public filter can't be moved under a private filter.")])]]))

(defn- this-filter-section [form source-entity]
  (let [options @(re-frame/subscribe [::view-editor.subs/auto-filter-options])
        label-by-value (into {} (map (juxt :value :label)) options)
        ptql (:view-ptql form)
        name (:name form)]
    [collapsible-card
     {:label (get-translation "view-editor.move.this-filter" "This Filter")
      :tooltip (get-translation "view-editor.move.this-filter.tooltip"
                                "The filter's own criteria. Moving doesn't change them.")
      :hint (get-translation "view-editor.combined.read-only" "Read Only")
      :track-expanded "This Filter Expanded"
      :track-collapsed "This Filter Collapsed"}
     (when-not (str/blank? name)
       [tooltip/mux-tooltip {} name
        (fn [props]
          [:div.view-editor-parent-display props name])])
     (cond
       (= :auto-filter (:mode form))
       [:div.view-editor-section-empty-note
        (str (get-translation "view-editor.auto-filter.name-prefix" "Auto Filter by") " "
             (str/join " / " (map #(label-by-value % %) (:auto-filter form))))]

       (str/blank? ptql)
       [:div.view-editor-section-empty-note
        (get-translation "view-editor.effective.no-criteria" "No criteria defined yet.")]

       :else
       [ptql-section {:value ptql :read-only? true :source-entity source-entity}])]))

(defn- move-body [form source-entity]
  [:div.view-editor-body
   [parent-picker-row form source-entity]
   [parent-section form source-entity]
   [this-filter-section form source-entity]
   [effective-section form source-entity {:open? true}]])

(defn- manual-filter-section [form mode source-entity]
  (let [render-mode @(re-frame/subscribe [::view-editor.subs/filter-render-mode])
        ptql-error @(re-frame/subscribe [::view-editor.subs/ptql-error])]
    (case render-mode
      :basic [bb/basic-filter-section form]
      :advanced [ptql-section
                 {:value (:view-ptql form)
                  :read-only? false
                  :auto-focus? (= mode :edit)
                  :on-change #(re-frame/dispatch [::events/view-ptql-changed %])
                  :on-parse-result #(re-frame/dispatch [::events/set-ptql-error %])
                  :parse-error ptql-error
                  :source-entity source-entity}])))

(defn- criteria-section [form mode source-entity]
  (let [render-mode @(re-frame/subscribe [::view-editor.subs/filter-render-mode])]
    [:div.view-editor-section-row
     [:div.view-editor-section-gutter
      [:span (get-translation "view-editor.criteria" "Criteria")]]
     [:div.view-editor-section-content
      (when (= render-mode :advanced)
        [:div.view-editor-criteria-tag-row
         [:span.view-editor-mode-tag (get-translation "view-editor.edit-mode" "Edit Mode")]])
      [manual-filter-section form mode source-entity]]]))

(defn- fields-section [form]
  (let [available-fields @(re-frame/subscribe [::view-editor.subs/available-fields])
        custom-columns? (seq (:definition form))]
    [collapsible-card
     {:label (get-translation "view-editor.default-fields" "Custom Columns")
      :tooltip (get-translation "view-editor.default-fields.tooltip"
                                "Choose which columns to display by default for this filter.")
      :hint (if custom-columns?
              (get-translation "view-editor.default-fields.custom" "Custom Columns")
              (get-translation "view-editor.default-fields.default" "Using Default Columns"))
      :track-expanded "Columns Settings Expanded"
      :track-collapsed "Columns Settings Collapsed"}
     nil
     [column-picker/column-picker
      {:empty-hint (get-translation "view-editor.default-fields.empty-hint"
                                    "No columns selected - the project's default columns for this entity type will be used.")
       :available-fields available-fields
       :selected (or (:definition form) [])
       :on-change #(re-frame/dispatch [::events/set-definition %])}]]))

(defn- view-editor-body []
  (let [form @(re-frame/subscribe [::view-editor.subs/form])
        loading? @(re-frame/subscribe [::view-editor.subs/loading?])
        mode @(re-frame/subscribe [::view-editor.subs/mode])
        source-entity @(re-frame/subscribe [::view-editor.subs/entity-type])
        af-mode? (= :auto-filter (:mode form))]
    (cond
      loading? [:div.view-editor-loading
                [spinners/spinner-with-text {:size :modal}]]
      (= :move mode) [move-body form source-entity]
      :else [:div.view-editor-body
             [name-row form mode]
             [filter-type-row form]
             (if af-mode?
               [:<>
                [:div.view-editor-criteria-header-row
                 [:span.view-editor-section-title (get-translation "view-editor.select-fields" "Select Fields")]]
                (when source-entity [parent-section form source-entity])
                [auto-filter-section form]]
               [:<>
                [filter-criteria-header form]
                (when source-entity [parent-section form source-entity])
                [criteria-section form mode source-entity]
                [effective-section form source-entity]])
             [fields-section form]])))

(defn view-editor-modal []
  (let [open? @(re-frame/subscribe [::view-editor.subs/open?])
        mode @(re-frame/subscribe [::view-editor.subs/mode])
        move? (= :move mode)
        cancel! #(do (track-action! (if move? "Move Filter Cancelled" "Filter Editor Cancelled"))
                     (re-frame/dispatch [::events/close]))
        saving? @(re-frame/subscribe [::view-editor.subs/saving?])
        error @(re-frame/subscribe [::view-editor.subs/error])
        source-entity @(re-frame/subscribe [::view-editor.subs/entity-type])]
    [modal/bootstrap-modal
     {:is-open? open?
      :on-request-close cancel!
      :close-button? true
      :centered? true
      :scrollable? true
      :class (str "view-editor-modal" (when move? " view-editor-move-modal"))}
     [:div.view-editor-modal-title-area
      [:div.view-editor-modal-header-row
       [:span.view-editor-modal-header-icon [icons/mux-tab-filter]]
       [:div
        [:h2.view-editor-modal-title
         (if move?
           (get-translation "view-editor.move.title" "Move Filter")
           (get-translation "view-editor.title" "Filter Editor"))
         (when source-entity [:span.view-editor-modal-entity " | " source-entity])]
        [:span.view-editor-modal-subtitle
         (if move?
           (get-translation "view-editor.move.subtitle"
                            "Moving a filter within the filter tree changes its criteria and, as a result, its results.")
           (get-translation "view-editor.subtitle"
                            "Filters and columns are saved together as a reusable filter."))]]]]
     [:<>
      (when error [:div.view-editor-error error])
      [view-editor-body]]
     [:div.view-editor-footer
      [buttons/button {:text (get-translation "view-editor.cancel" "Cancel")
                       :on-click cancel!}]
      (let [disable-reason @(re-frame/subscribe [::view-editor.subs/save-disabled-reason])
            tooltip (when disable-reason
                      (case disable-reason
                        :empty-name (get-translation "view-editor.save-disabled.empty-name"
                                                     "Name cannot be empty")
                        :incomplete (get-translation "view-editor.save-disabled.incomplete"
                                                     "Please select a value for all fields")
                        :invalid-ptql (get-translation "view-editor.save-disabled.invalid-ptql"
                                                       "Fix the filter syntax error before saving")
                        :no-auto-filter (get-translation "view-editor.save-disabled.no-auto-filter"
                                                         "Select at least one auto-filter field")
                        :parent-unchanged (get-translation "view-editor.save-disabled.parent-unchanged"
                                                           "Select a different parent filter")
                        :private-parent (get-translation "view-editor.move.private-parent"
                                                         "A public filter can't be moved under a private filter.")))]
        [buttons/button {:primary? true
                         :text (cond
                                 (and move? saving?) (get-translation "view-editor.move.saving" "Moving...")
                                 move? (get-translation "view-editor.move.save" "Move")
                                 saving? (get-translation "view-editor.saving" "Saving...")
                                 :else (get-translation "view-editor.save" "Save"))
                         :tooltip tooltip
                         :test-id "filter-save-btn"
                         :disabled? (some? disable-reason)
                         :loading? saving?
                         :on-click #(do (track-action! (if move? "Filter Moved" "Filter Editor Saved"))
                                        (re-frame/dispatch [::events/save]))}])]]))

(comment
  (re-frame/dispatch [::events/open-edit 21625])

  (re-frame/dispatch [::events/open-create {:parent-id 82051
                                            :entity-type "Issue"
                                            :seed {:name "Test seed name 4"
                                                   :view-ptql "Priority = \"High\""}}])
  (re-frame/dispatch [::events/open-create {:parent-id 82051
                                            :entity-type "Issue"
                                            :seed {:name "Test seed name 5"
                                                   :view-ptql "LINKED TO TestSet"}}])
  (re-frame/dispatch [::events/open-create {:parent-id 82051
                                            :entity-type "Issue"
                                            :seed {:name "Test seed name 5"
                                                   :view-ptql "COUNT(LINKED TO TestSet WHERE ('Run Status' = \"BLOCKED\")) > 0"}}]))
